import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Rating } from "primereact/rating";
import React, { useCallback, useEffect, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import GalleryModal from "../ListProperties/GalleryModal";
import type { Property } from "../../models/property.model";
import type { ApplicationUser } from "../../models/user.model";
import { slugify } from "../../utils/slug";
import LoadingSpinner from "../LoadingSpinner";
import { reservationShortlistService } from "../../service/reservationShortlist.service";
import "./PropertyGrid.css";

// Component to display distance between user and property
const DistanceDisplay: React.FC<{
  propertyAddress: string;
  calculateDistance: (address: string) => Promise<string>;
}> = ({ propertyAddress, calculateDistance }) => {
  const [distance, setDistance] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const getDistance = async () => {
      setLoading(true);
      try {
        const calculatedDistance = await calculateDistance(propertyAddress);
        setDistance(calculatedDistance);
      } catch (error) {
        console.error("Error calculating distance:", error);
        setDistance("");
      } finally {
        setLoading(false);
      }
    };

    if (propertyAddress) {
      getDistance();
    }
  }, [propertyAddress, calculateDistance]);

  if (loading) {
    return <small className="text-muted">Calculating...</small>;
  }

  if (!distance) {
    return null;
  }

  return (
    <>
      <Icon.GeoAlt style={{ marginRight: "5px" }} />
      <span>{distance}</span>
    </>
  );
};

interface SortFilter {
  name: string;
  code: string;
}

interface PropertyGridProps {
  properties: Property[];
  allProperties: Property[];
  loading: boolean;
  userDetails: ApplicationUser | null;
  onPropertiesChange: (properties: Property[]) => void;
  dateRange?: [Date | null, Date | null] | null;
  selectedRoomType?: { id: string | number; name: string } | null;
  selectedRoomTypes?: { id: string | number; name: string }[];
  onPropertyMapView?: (propertyId: string | number) => void;
  calculateDistanceToProperty?: (propertyAddress: string) => Promise<string>;
  onPropertyRouteView?: (property: Property) => void;
}

const PropertyGrid: React.FC<PropertyGridProps> = ({
  properties,
  allProperties,
  loading,
  userDetails,
  onPropertiesChange,
  dateRange,
  selectedRoomType,
  selectedRoomTypes,
  onPropertyMapView,
  calculateDistanceToProperty,
  onPropertyRouteView,
}) => {
  const navigate = useNavigate();

  // Redux selectors for logged-in user info
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";
  const authUser = useSelector((state: any) => state.auth?.user) ?? null;

  // B2B user booking data from user details
  const [userCheckIn, setUserCheckIn] = useState<string>("");
  const [userCheckOut, setUserCheckOut] = useState<string>("");
  const [userNoOfAdults, setUserNoOfAdults] = useState<number>(0);
  const [userNoOfChildrens, setUserNoOfChildrens] = useState<number>(0);

  const [favorites, setFavorites] = useState<string[]>([]);
  const [shortlistedPropertyIds, setShortlistedPropertyIds] = useState<Set<string>>(new Set());
  const [selectedFilter, setSelectedFilter] = useState<SortFilter | null>(null);
  
  // Property Gallery Modal State
  const [galleryModal, setGalleryModal] = useState<{
    visible: boolean;
    images: Array<{
      id: number;
      imageUrl: string;
      type: string;
      name: string;
      category?: string;
    }>;
    initialImageIndex: number;
    propertyData?: any;
  }>({
    visible: false,
    images: [],
    initialImageIndex: 0,
    propertyData: null,
  });

  const cities: SortFilter[] = [
    { name: "Low to High", code: "NY" },
    { name: "High to Low", code: "RM" },
  ];

  // Fetch shortlisted items and populate favorites
  const fetchShortlistedItems = useCallback(async () => {
    try {
      const shortlistedItems = await reservationShortlistService.getMyShortlistedItems();
      console.log("Fetched shortlisted items:", shortlistedItems);
      
      // Extract property IDs from shortlisted rooms
      const propertyIds = new Set<string>();
      shortlistedItems.forEach((item) => {
        if (item.room?.propertyId) {
          propertyIds.add(item.room.propertyId.toString());
        }
      });
      
      console.log("Shortlisted property IDs:", Array.from(propertyIds));
      setShortlistedPropertyIds(propertyIds);
      setFavorites(Array.from(propertyIds));
    } catch (error) {
      console.error("Error fetching shortlisted items:", error);
    }
  }, []);

  // Fetch shortlisted items on mount
  useEffect(() => {
    if (userRole === "B2BUser") {
      fetchShortlistedItems();
    }
  }, [userRole, fetchShortlistedItems]);

  // Extract user booking data from userDetails
  useEffect(() => {
    if (userDetails?.address && userDetails.address.length > 0) {
      const userAddress = userDetails.address[0]; // Get first address
      console.log("PropertyGrid user address object:", userAddress);
      if (userAddress.checkIn) {
        console.log(
          "PropertyGrid setting userCheckIn to:",
          userAddress.checkIn
        );
        const checkInStr =
          typeof userAddress.checkIn === "string"
            ? userAddress.checkIn
            : userAddress.checkIn instanceof Date
            ? userAddress.checkIn.toISOString()
            : String(userAddress.checkIn);
        setUserCheckIn(checkInStr);
      }
      if (userAddress.checkOut) {
        console.log(
          "PropertyGrid setting userCheckOut to:",
          userAddress.checkOut
        );
        const checkOutStr =
          typeof userAddress.checkOut === "string"
            ? userAddress.checkOut
            : userAddress.checkOut instanceof Date
            ? userAddress.checkOut.toISOString()
            : String(userAddress.checkOut);
        setUserCheckOut(checkOutStr);
      }

      if (
        userAddress.noOfAdults !== undefined &&
        userAddress.noOfAdults !== null
      ) {
        console.log(
          "PropertyGrid setting userNoOfAdults to:",
          userAddress.noOfAdults
        );
        const adultsVal = Number(userAddress.noOfAdults);
        setUserNoOfAdults(adultsVal);
      }
      if (
        userAddress.noOfChildrens !== undefined &&
        userAddress.noOfChildrens !== null
      ) {
        console.log(
          "PropertyGrid setting userNoOfChildrens to:",
          userAddress.noOfChildrens
        );
        const childVal = Number(userAddress.noOfChildrens);
        setUserNoOfChildrens(childVal);
      }
    }
  }, [userDetails]);

  // Handle view details navigation
  const handleViewDetails = useCallback(
    (property: Property) => {
      const checkInValue =
        userRole === "B2BUser"
          ? userCheckIn
          : dateRange?.[0]?.toISOString() || "";
      const checkOutValue =
        userRole === "B2BUser"
          ? userCheckOut
          : dateRange?.[1]?.toISOString() || "";

      console.log("PropertyGrid Navigation state values:", {
        userRole,
        checkInValue,
        checkOutValue,
        userNoOfAdults,
        userNoOfChildrens,
        propertyId: property.id,
        propertyName: property.name,
      });

      // If room types are selected in filter, pass all selected room type IDs
      const roomTypeIds = selectedRoomTypes && selectedRoomTypes.length > 0
        ? selectedRoomTypes.map(rt => Number(rt.id))
        : (selectedRoomType ? [Number(selectedRoomType.id)] : []);

      navigate(`/property/${slugify(property.name)}-${property.id}`, {
        state: {
          propertyId: property.id,
          checkIn: checkInValue,
          checkOut: checkOutValue,
          roomTypeIds: roomTypeIds.length > 0 ? roomTypeIds : undefined,
          noOfAdults: userRole === "B2BUser" ? userNoOfAdults : undefined,
          noOfChildrens: userRole === "B2BUser" ? userNoOfChildrens : undefined,
        },
      });
    },
    [
      navigate,
      userRole,
      userCheckIn,
      userCheckOut,
      userNoOfAdults,
      userNoOfChildrens,
      dateRange,
      selectedRoomType,
      selectedRoomTypes,
    ]
  );

  // Handle view map functionality
  const handleViewMap = useCallback(
    (property: Property) => {
      console.log(
        "PropertyGrid highlighting property on map:",
        property.name,
        property.address
      );
      if (onPropertyMapView && property.id) {
        onPropertyMapView(property.id);
      }
    },
    [onPropertyMapView]
  );

  // Handle view route functionality
  const handleViewRoute = useCallback(
    (property: Property) => {
      if (onPropertyRouteView) {
        onPropertyRouteView(property);
      }
    },
    [onPropertyRouteView]
  );

  // Helper function to process property images (same logic as ListProperties)
  const getPropertyImageUrls = useCallback((property: any): string[] => {
    if (!property) return [];
    const raw = property as any;
    const sourceImages =
      raw.propertyImages ||
      raw.PropertyImages ||
      raw.imageDtos ||
      raw.ImageDtos ||
      raw.images ||
      null;
    const imgs: string[] = [];

    const toUrl = (rawUrl: any) => {
      if (!rawUrl) return undefined;
      const s = String(rawUrl).trim();
      if (!s) return undefined;
      if (/^https?:\/\//i.test(s) || s.startsWith("/")) return s;
      if (
        /^[\w\-]+\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(s) ||
        s.indexOf("/") === -1
      )
        return `/Images/${s}`;
      return s;
    };

    if (Array.isArray(sourceImages) && sourceImages.length) {
      if (typeof sourceImages[0] === "string") {
        sourceImages.forEach((s: string) => {
          const u = toUrl(s);
          if (u) imgs.push(u);
        });
      } else {
        const getUrl = (pi: any) =>
          toUrl(
            pi &&
              (pi.imageUrl ||
                pi.ImageUrl ||
                pi.url ||
                pi.Url ||
                pi.src ||
                pi.path ||
                pi.fileName ||
                pi.FileName ||
                pi.filePath ||
                pi.FilePath)
          );
        const main = sourceImages.find(
          (pi: any) => pi && (pi.isMainImage || pi.isMain || pi.IsMainImage)
        );
        if (main) {
          const m = getUrl(main);
          if (m) imgs.push(m);
        }
        sourceImages.forEach((pi: any) => {
          const u = getUrl(pi);
          if (!u) return;
          if (main && u === getUrl(main)) return;
          imgs.push(u);
        });
      }
    } else if (
      Array.isArray(raw.propertyImages) &&
      raw.propertyImages.length &&
      typeof raw.propertyImages[0] === "string"
    ) {
      raw.propertyImages.forEach((s: string) => {
        const u = toUrl(s);
        if (u) imgs.push(u);
      });
    } else if (Array.isArray(raw.images) && raw.images.length) {
      raw.images.forEach((s: string) => {
        const u = toUrl(s);
        if (u) imgs.push(u);
      });
    } else if (raw.imageUrl) {
      const u = toUrl(raw.imageUrl);
      if (u) imgs.push(u);
    }

    // Dedupe while preserving order
    const uniq = Array.from(new Set(imgs)).filter(Boolean) as string[];
    return uniq.length > 0 ? uniq : ["/Images/no-image-placeholder.png"];
  }, []);

  // Helper function to get property type name
  const getPropertyTypeName = useCallback((property: Property): string => {
    return property.propertyType?.name || "Property";
  }, []);

  // Helper function to get location name
  const getLocationName = useCallback((property: Property): string => {
    return property.location?.name || property.city?.name || "Location";
  }, []);

  // Open Property Gallery Modal
  const openPropertyGallery = (property: Property, clickedImageIndex: number = 0) => {
    const propertyImages = getPropertyImageUrls(property);
    const propertyName = property.name || "Property";
    const propertyType = getPropertyTypeName(property);

    // Create image objects for the modal
    const propertyImageData = propertyImages.map((imageUrl, imgIdx) => ({
      id: imgIdx,
      imageUrl,
      type: propertyType,
      name: propertyName,
      category: "Property Image",
    }));

    setGalleryModal({
      visible: true,
      images: propertyImageData,
      initialImageIndex: clickedImageIndex,
      propertyData: property,
    });
  };

  // Close Property Gallery Modal
  const closePropertyGallery = () => {
    setGalleryModal({
      visible: false,
      images: [],
      initialImageIndex: 0,
      propertyData: null,
    });
  };

  // Helper function to get room count info
  const getRoomInfo = useCallback((property: Property): string => {
    const rooms = (property as any).roomTypes || [];
    if (Array.isArray(rooms) && rooms.length > 0) {
      // Get unique room categories/types
      const categories = rooms
        .map((r: any) => r.category?.name || r.categoryName || r.name)
        .filter(Boolean);
      const uniqueCategories = Array.from(new Set(categories));
      if (uniqueCategories.length > 0) {
        return uniqueCategories.slice(0, 2).join(", ");
      }
    }
    return "N/A";
  }, []);
  return (
    <>
      <div className="property-grid d-flex flex-column justify-content-start mb-3 mt-0 flex-fill">
        <div className="property-grid-header d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            {loading
              ? "Loading properties..."
              : `${properties.length} Listings`}
          </h6>
          <div className="d-flex justify-content-end align-items-center gap-2">
            <button
              className="view-filter btn-light d-inline-block text-nowrap text-link"
              title="Filter properties"
              aria-label="Filter properties"
            >
              <Icon.Filter />
            </button>
            <div className="sort-dropdown">
              <Dropdown
                value={selectedFilter}
                onChange={(e: DropdownChangeEvent) =>
                  setSelectedFilter(e.value)
                }
                options={cities}
                optionLabel="name"
                placeholder="Sort by"
                appendTo="self"
                className="w-full md:w-14rem"
              />
            </div>
          </div>
        </div>
        <div className="properties-container flex-grow-1">
          {loading ? (
            <LoadingSpinner size="md" text="Loading properties..." />
          ) : properties.length === 0 ? (
            <div className="text-center py-4">
              <p>No properties available</p>
            </div>
          ) : (
            properties.map((property) => {
              const images = getPropertyImageUrls(property);
              const propertyType = getPropertyTypeName(property);
              const location = getLocationName(property);
              const roomInfo = getRoomInfo(property);
              console.log("Rendering property:", property);
              return (
                <div key={property.id} className="property-card">
                  {/* Shortlist indicator at card top-right (outside image slider) */}
                  {userRole === "B2BUser" && (
                    <div
                      className={`card-favorite-btn${
                        favorites.includes(property.id?.toString() || "")
                          ? " active"
                          : ""
                      }`}
                      title={favorites.includes(property.id?.toString() || "") 
                        ? "Property has shortlisted rooms" 
                        : "No shortlisted rooms"}
                    >
                      {favorites.includes(property.id?.toString() || "") ? (
                        <Icon.HeartFill />
                      ) : (
                        <Icon.Heart />
                      )}
                    </div>
                  )}
                  {/* Image Container */}
                  <div className="property-image-container">
                    {(() => {
                      const pid = String(property.id ?? "p");
                      const prevCls = `prop-${pid}-nav-prev`;
                      const nextCls = `prop-${pid}-nav-next`;
                      return (
                        <>
                          {/* Explicit navigation elements should exist before Swiper initializes */}
                          <div
                            className={`swiper-button-prev ${prevCls}`}
                            aria-label="Previous image"
                          />
                          <div
                            className={`swiper-button-next ${nextCls}`}
                            aria-label="Next image"
                          />
                          <Swiper
                            modules={[Navigation, Pagination]}
                            spaceBetween={0}
                            slidesPerView={1}
                            pagination={{ clickable: true }}
                            navigation={{
                              prevEl: `.${prevCls}`,
                              nextEl: `.${nextCls}`,
                            }}
                            style={{ height: "100%" }}
                          >
                            {images.map((image, index) => (
                              <SwiperSlide key={index}>
                                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                  <img
                                    src={image}
                                    alt={`${property.name} - Image ${index + 1}`}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src =
                                        "/Images/no-image-placeholder.png";
                                    }}
                                    onClick={() => openPropertyGallery(property, index)}
                                    style={{ cursor: 'zoom-in' }}
                                  />
                                  <div className="gallery-view-icon">
                                    <Icon.Eye size={20} />
                                  </div>
                                </div>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </>
                      );
                    })()}
                  </div>

                  {/* Property Details & Actions Container */}
                  <div className="property-details-container">
                    {/* Property Details */}
                    <h3 className="property-title">{property.name}</h3>
                    <div className="property-location-info">
                      <span><i className="pi pi-building me-1" style={{ fontSize: '0.8rem', color: '#87CEEB' }}></i>{propertyType}</span>
                      <span className="dot">•</span>
                      <span><i className="pi pi-map-marker me-1" style={{ fontSize: '0.8rem', color: '#87CEEB' }}></i>{property.location?.name}</span>
                      {property.location?.zone?.name && (
                        <>
                          <span className="dot">•</span>
                          <span><i className="pi pi-map me-1" style={{ fontSize: '0.8rem', color: '#87CEEB' }}></i>{property.location.zone.name}</span>
                        </>
                      )}
                      {property.cityName && (
                        <>
                          <span className="dot">•</span>
                          <span><i className="pi pi-globe me-1" style={{ fontSize: '0.8rem', color: '#87CEEB' }}></i>{property.cityName}</span>
                        </>
                      )}
                    </div>
                    <div className="property-rating">
                      {(() => {
                        const raw = (property as any)?.rating ?? 4;
                        const numeric = Number.isFinite(Number(raw))
                          ? Number(raw)
                          : 4;
                        const stars = Math.max(
                          0,
                          Math.min(5, Math.round(numeric))
                        );
                        return (
                          <>
                            <Rating value={stars} readOnly cancel={false} />
                            <span className="rating-value">
                              {numeric.toFixed(1)}/5
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    <div className="property-meta">
                      <Icon.Building
                        style={{ marginRight: "6px", fontSize: "16px" }}
                      />
                      {roomInfo}
                      {calculateDistanceToProperty &&
                        userDetails?.address &&
                        userDetails.address.length > 0 &&
                        property.address && (
                          <>
                            <DistanceDisplay
                              propertyAddress={property.address}
                              calculateDistance={calculateDistanceToProperty}
                            />
                          </>
                        )}
                    </div>
                    {/* Action Buttons */}
                    <div className="property-actions">
                      <button
                        className="view-details-btn"
                        onClick={() => handleViewDetails(property)}
                      >
                        View details
                      </button>
                      <button
                        className="view-map-btn"
                        onClick={() => handleViewMap(property)}
                      >
                        View map
                      </button>
                      <button
                        className="view-route-btn"
                        onClick={() => handleViewRoute(property)}
                      >
                        View route
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* Property Gallery Modal */}
      <GalleryModal
        visible={galleryModal.visible}
        onHide={closePropertyGallery}
        images={galleryModal.images}
        initialImageIndex={galleryModal.initialImageIndex}
        data={galleryModal.propertyData}
        mode="property"
      />
    </>
  );
};
export default PropertyGrid;
